import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Card from "../../components/ui/Card.jsx";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { verifyLoginOtp } from "../../api/authorTwoFa.js";
import { completeLenderLogin } from "./lenderAuthUtils.js";
import { signOut } from "../../api/client.js";
import { getLoginError } from "../../utils/errorMessages.js";
import { hasUsableApiCredentials } from "../../utils/loginAuth.js";

export default function LenderVerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const tmpId = state.tmp_id || "";
  const email = state.email || "";
  const method = state.method || "email";
  const remember = state.remember !== false;
  const redirectPath = state.redirectPath || "/lender/overview";

  const [otp, setOtp] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!tmpId || !email) {
      navigate("/signin", { replace: true });
    }
  }, [tmpId, email, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const code = otp.trim();
    if (!code) {
      setError("Please enter the verification code.");
      return;
    }
    if (!tmpId) {
      navigate("/signin", { replace: true });
      return;
    }

    setLoading(true);
    try {
      const authPayload = await verifyLoginOtp({ tmp_id: tmpId, otp: code });

      if (!authPayload?.user || !hasUsableApiCredentials(authPayload)) {
        setError("Invalid or expired code. Please sign in again.");
        return;
      }

      await completeLenderLogin(
        email,
        { headers: {}, data: authPayload },
        authPayload,
        remember,
        redirectPath
      );
    } catch (err) {
      if (err?.message === "LOGIN_CREDENTIALS_MISSING") {
        setError("Invalid or expired code. Please sign in again.");
        return;
      }
      if (err?.message === "SESSION_MISMATCH") {
        await signOut();
        setError("Session mismatch detected. Please clear your browser cookies and try again.");
        return;
      }
      if (err?.message === "SESSION_VERIFY_FAILED") {
        await signOut();
        setError("Could not verify your session. Please try again.");
        return;
      }
      console.error("OTP verify error:", err);
      setError(getLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!tmpId || !email) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4 sm:p-6">
      <Card className="w-full max-w-md rounded-2xl border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-sky-500" />
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
              Verify your sign-in
            </h2>
          </div>
          <p className="mt-1 text-xs sm:text-sm text-slate-600">
            {method === "email"
              ? `Enter the code we sent to ${email}.`
              : "Enter your verification code to continue."}
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-3 sm:space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs sm:text-sm font-medium text-slate-700">
              Verification code
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\s/g, ""))}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-center text-lg tracking-[0.3em] outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50"
              placeholder="000000"
              maxLength={8}
              disabled={loading}
              autoFocus
            />
          </label>

          {error && <p className="text-xs sm:text-sm text-rose-600">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-500 px-4 py-2.5 text-sm sm:text-base font-semibold text-white shadow hover:bg-sky-600 disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {loading ? "Verifying..." : "Verify and continue"}
          </button>

          <Link
            to="/signin"
            className="inline-flex w-full items-center justify-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </Link>
        </form>
      </Card>
    </div>
  );
}
